import { pileDePolice } from '../../lib/polices';
import { DEVISE, fmt } from '../../lib/currency';
import { printDoc } from '../../lib/print';
import { C } from '../../theme/palette';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';

const ligne = (strong) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
  borderBottom: '1px solid ' + C.border,
  fontSize: strong ? 11.5 : 10.5,
  fontWeight: strong ? 800 : 500,
});

function Row({ l, v, c, strong }) {
  return (
    <div style={ligne(strong)}>
      <span style={{ color: strong ? C.ink : C.grey }}>{l}</span>
      <span style={{ color: c || C.ink, whiteSpace: 'nowrap' }}>
        {DEVISE} {fmt(v || 0)}
      </span>
    </div>
  );
}

function H2({ t }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: C.navy,
        marginTop: 15,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {t}
    </div>
  );
}

/**
 * Bilan et compte de résultat d'un exercice, au format A4.
 *
 * Le document sert à la déclaration d'impôts : il présente ce qui a été figé
 * à la clôture, et non les chiffres du jour. C'est pourquoi il ne recalcule
 * rien — il affiche la clôture telle qu'elle a été conservée.
 */
export function ClotureA4({ data, soc, onClose }) {
  const d = data || {};
  const an = d.annee || new Date().getFullYear();

  return (
    <PleinEcran>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          paddingTop: 'var(--st-haut)',
          zIndex: 9999,
          background: '#5b5f66',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="clotNoPrint"
          style={{
            background: C.navy,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ‹ Retour
          </button>
          <button
            onClick={printDoc}
            style={{
              background: C.green,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 11.5,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            🖨 PDF
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 10px' }}>
          <div
            className="a4-sheet"
            style={{
              maxWidth: 680,
              margin: '0 auto',
              background: '#fff',
              borderRadius: 4,
              padding: '26px 30px',
              color: C.ink,
              fontFamily: pileDePolice(soc?.docFont) || 'inherit',
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
              {soc?.logo ? <img src={soc.logo} alt="" style={{ maxHeight: 46 }} /> : null}
              <div>
                <div style={{ fontSize: 13, fontWeight: 800 }}>{soc?.nom}</div>
                <div style={{ fontSize: 9.5, color: C.grey }}>
                  {soc?.adr}
                  {soc?.npa ? ' · ' + soc.npa : ''}
                </div>
                {soc?.tva ? (
                  <div style={{ fontSize: 9, color: C.muted }}>N° TVA {soc.tva}</div>
                ) : null}
              </div>
            </div>

            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: C.navy,
                borderBottom: '2px solid ' + C.navy,
                paddingBottom: 5,
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Clôture {an}
            </div>
            <div style={{ fontSize: 10, color: C.grey, marginBottom: 2 }}>
              Compte de résultat &amp; bilan · 01.01.{an} – 31.12.{an}
              {d.dateCloture ? ' · clôturé le ' + d.dateCloture : ''}
            </div>

            <H2 t="Compte de résultat — Produits" />
            <Row l="Chiffre d'affaires (HT)" v={d.ca} c={C.green} />
            {d.ventes > 0 ? <Row l="Ventes caisse" v={d.ventes} c={C.green} /> : null}
            <Row l="Total produits" v={d.produits} strong />

            <H2 t="Compte de résultat — Charges" />
            <Row l="Achats de marchandises" v={d.achats} c={C.red} />
            <Row l="Frais généraux" v={d.frais} c={C.red} />
            {d.vehicules > 0 ? (
              <Row l="Véhicules (leasing + amortissement)" v={d.vehicules} c={C.red} />
            ) : null}
            {/* Les salaires avec les charges patronales : « Salaires bruts »
              annonçait le brut seul. */}
            <Row l="Salaires & charges sociales" v={d.salaires} c={C.red} />
            {d.comptable > 0 ? <Row l="Honoraires comptable" v={d.comptable} c={C.red} /> : null}
            <Row l="Total charges" v={d.charges} strong />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 8,
                background: d.resultat >= 0 ? C.greenL : C.redL,
              }}
            >
              <b style={{ fontSize: 12 }}>
                {d.resultat >= 0 ? 'Bénéfice' : 'Perte'} de l&apos;exercice
              </b>
              <b style={{ fontSize: 13, color: d.resultat >= 0 ? C.green : C.red }}>
                {DEVISE} {fmt(d.resultat || 0)}
              </b>
            </div>

            <H2 t="Bilan — Actif" />
            <Row l="Banque" v={d.banque} />
            <Row l="Caisse" v={d.caisse} />
            <Row l="Débiteurs (net des acomptes reçus)" v={d.debiteurs} />
            <Row l="Stock de marchandises" v={d.stock} />
            {d.immobilisations > 0 ? (
              <Row l="Véhicules et leasing payé d'avance" v={d.immobilisations} />
            ) : null}
            <Row l="Total actif" v={d.actif} strong />

            <H2 t="Bilan — Passif" />
            <Row l="Dettes" v={d.dettes} c={C.red} />
            {d.acomptesAvance > 0 ? (
              <Row l="Acomptes reçus d'avance (travaux de l'année suivante)" v={d.acomptesAvance} />
            ) : null}
            <Row l="Fonds propres" v={d.fondsPropres} strong />

            <div style={{ fontSize: 8, color: C.muted, marginTop: 18, lineHeight: 1.5 }}>
              Document établi à partir des écritures saisies dans SwissTech. Il ne remplace pas les
              comptes établis par une fiduciaire et n&apos;engage pas son auteur. Conservation
              décennale des pièces comptables (CO art. 958f).
            </div>
            {/*
            Le bilan part chez la fiduciaire ou au fisc signé par le patron :
            sans pied de signature, il fallait l'annoter à la main.
          */}
            <div
              style={{
                marginTop: 36,
                paddingTop: 14,
                borderTop: '1px solid ' + C.border,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <div style={{ fontSize: 9, color: C.muted }}>{soc?.nom || ''}</div>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    borderTop: '1px solid ' + C.muted,
                    width: 180,
                    paddingTop: 5,
                    fontSize: 9,
                    color: C.muted,
                  }}
                >
                  Signature &amp; timbre
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PleinEcran>
  );
}
